// src/components/workflows/WorkflowEditor.jsx - Edit Mode Canvas for Workflows
import React, { useState, useCallback, useRef } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  BackgroundVariant,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

// Custom Nodes
import {
  EventWaitNode,
  SendEmailNode,
  SendSMSNode,
  WebhookNode,
  UpdateFieldNode,
  WaitNode,
  LogicGateNode,
  EntryNode,
  ExitNode,
  // Client automation nodes
  ScheduleTriggerNode,
  HttpRequestNode,
  WebhookReceiverNode,
} from './nodes';

// Store
import useWorkflowStore from '../../stores/WorkflowStore';

// UI Components
import { Button } from '../ui/button.tsx';
import { Plus, Save, AlertCircle } from 'lucide-react';

// Node types configuration
const nodeTypes = {
  eventWait: EventWaitNode,
  sendEmail: SendEmailNode,
  sendSMS: SendSMSNode,
  webhook: WebhookNode,
  updateField: UpdateFieldNode,
  wait: WaitNode,
  logicGate: LogicGateNode,
  entry: EntryNode,
  exit: ExitNode,
  // Client automation nodes
  scheduleTrigger: ScheduleTriggerNode,
  httpRequest: HttpRequestNode,
  webhookReceiver: WebhookReceiverNode,
};

// Transform frontend node types to backend format
const transformToBackendFormat = (nodes) => {
  return nodes.map(node => {
    // Action nodes: Convert type to 'action'
    if (['sendEmail', 'sendSMS', 'webhook', 'updateField'].includes(node.type)) {
      return {
        ...node,
        type: 'action'
      };
    }
    // Branch nodes: Convert type to 'branch'
    if (node.type === 'logicGate') {
      return {
        ...node,
        type: 'branch'
      };
    }
    // All other nodes stay the same
    return node;
  });
};

// Transform backend node format to frontend format
const transformFromBackendFormat = (nodes) => {
  return nodes.map(node => {
    // Action nodes: Convert based on actionType
    if (node.type === 'action' && node.data?.actionType) {
      const typeMap = {
        'send_email': 'sendEmail',
        'send_sms': 'sendSMS',
        'webhook': 'webhook',
        'update_customer': 'updateField'
      };
      return {
        ...node,
        type: typeMap[node.data.actionType] || 'action'
      };
    }
    // Branch nodes: Convert type to 'logicGate'
    if (node.type === 'branch') {
      return {
        ...node,
        type: 'logicGate'
      };
    }
    // All other nodes stay the same
    return node;
  });
};

// Default edge style (dark theme with purple accent)
const defaultEdgeOptions = {
  style: { stroke: '#8b5cf6', strokeWidth: 2 },
  type: 'smoothstep',
  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: '#8b5cf6',
  },
};

/**
 * WorkflowEditor - Edit Mode canvas for designing workflows
 *
 * Features:
 * - Visual workflow designer using React Flow
 * - Drag-and-drop node creation
 * - Auto-save functionality
 * - Stage gate model (customers live at stage gates, actions happen between)
 * - Dark theme with purple accents
 */
const WorkflowEditor = ({ workflowId }) => {
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  // Store connection
  const {
    workflows,
    selectedWorkflow,
    stages,
    loadWorkflowDetails,
    updateWorkflowConfig,
  } = useWorkflowStore();

  // Local state
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [validationError, setValidationError] = useState(null);

  // Refs for auto-save management
  const isAutoSavingRef = useRef(false);
  const hasLoadedInitialDataRef = useRef(false);
  const userHasMadeChangesRef = useRef(false);

  // NOTE: Don't load workflow here - parent (WorkflowCanvas) already loads it
  // We just consume selectedWorkflow from the store via the sync effect below

  // Sync selectedWorkflow from store to local nodes/edges
  React.useEffect(() => {
    if (selectedWorkflow && !isAutoSavingRef.current) {
      // Check if workflow has nodes (not just if flow_config exists)
      const hasNodes = selectedWorkflow.flow_config?.nodes?.length > 0;

      if (hasNodes) {
        const { nodes: flowNodes, edges: flowEdges } = selectedWorkflow.flow_config;
        // Transform backend format to frontend format
        const transformedNodes = transformFromBackendFormat(flowNodes || []);
        setNodes(transformedNodes);
        setEdges(flowEdges || []);
      } else {
        // Empty workflow - start with default entry node
        const entryNode = {
          id: 'entry-1',
          type: 'entry',
          position: { x: 50, y: 200 },
          data: {
            isEntry: true,
            triggerEvent: 'customer_created',
            conditions: [],
            label: 'Entry Point',
            description: 'Entry point',
          },
        };
        setNodes([entryNode]);
        setEdges([]);
      }

      // Mark that we've loaded initial data (don't auto-save yet)
      hasLoadedInitialDataRef.current = true;
      userHasMadeChangesRef.current = false;
    }

    // Reset auto-saving flag
    isAutoSavingRef.current = false;
  }, [selectedWorkflow, setNodes, setEdges]);

  // Wrapped handlers to track user changes
  const handleNodesChange = useCallback(
    (changes) => {
      onNodesChange(changes);

      // Mark that user made changes (only if initial data has loaded)
      if (hasLoadedInitialDataRef.current) {
        // Filter out React Flow's internal changes (dimensions, dragging state, etc.)
        // Only consider real user changes: add, remove, position, reset, replace
        const isUserChange = changes.some(change =>
          change.type === 'add' ||       // User added a node
          change.type === 'remove' ||    // User deleted a node
          change.type === 'position' ||  // User moved a node
          change.type === 'reset' ||     // User reset the flow
          change.type === 'replace'      // User edited node data (updateNodeData)
          // NOT: 'dimensions' (React Flow measures nodes)
          // NOT: 'select' (just clicking, not editing)
        );

        if (isUserChange) {
          userHasMadeChangesRef.current = true;
        }
      }
    },
    [onNodesChange]
  );

  const handleEdgesChange = useCallback(
    (changes) => {
      onEdgesChange(changes);

      // Mark that user made changes (only if initial data has loaded)
      if (hasLoadedInitialDataRef.current) {
        // For edges, all changes except 'select' are user-initiated
        const isUserChange = changes.some(change =>
          change.type === 'add' ||    // User connected nodes
          change.type === 'remove' || // User deleted edge
          change.type === 'reset'     // User reset the flow
          // NOT: 'select' (just clicking)
        );

        if (isUserChange) {
          userHasMadeChangesRef.current = true;
        }
      }
    },
    [onEdgesChange]
  );

  // Handle node connection
  const onConnect = useCallback(
    (params) => {
      setEdges((eds) => {
        // Remove any existing edge from the same source handle (enforce one connection per handle)
        const filteredEdges = eds.filter(
          edge => !(edge.source === params.source && edge.sourceHandle === params.sourceHandle)
        );

        // Add the new edge
        const newEdges = addEdge({
          ...params,
          ...defaultEdgeOptions,
        }, filteredEdges);

        return newEdges;
      });

      // Mark that user made changes
      if (hasLoadedInitialDataRef.current) {
        userHasMadeChangesRef.current = true;
      }
    },
    [setEdges]
  );

  // Handle node selection
  const onNodeClick = useCallback(() => {
    // All nodes are self-contained with inline editing, so don't show properties panel
    setSelectedNode(null);
  }, []);

  // Handle pane click (deselect)
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  // Validation: Check entry point rules
  const validateEntryPoint = useCallback((currentNodes, currentEdges) => {
    const entryNodes = currentNodes.filter(node => node.type === 'entry');

    // Rule 1: Must have exactly one entry node
    if (entryNodes.length === 0) {
      return { valid: false, error: 'Workflow must have an entry point' };
    }
    if (entryNodes.length > 1) {
      return { valid: false, error: 'Workflow can only have one entry point' };
    }

    // Rule 2: Entry node must have no incoming edges (must be at the start)
    const entryNode = entryNodes[0];
    const hasIncomingEdge = currentEdges.some(edge => edge.target === entryNode.id);
    if (hasIncomingEdge) {
      return { valid: false, error: 'Entry point must be at the start (no incoming connections)' };
    }

    // Rule 3: Entry node must have triggerEvent configured
    if (!entryNode.data?.triggerEvent) {
      return { valid: false, error: 'Entry point must have a trigger event selected' };
    }

    return { valid: true, error: null };
  }, []);

  // Validate on nodes/edges change
  React.useEffect(() => {
    if (nodes.length === 0) {
      setValidationError(null);
      return;
    }

    const validation = validateEntryPoint(nodes, edges);
    setValidationError(validation.valid ? null : validation.error);
  }, [nodes, edges, validateEntryPoint]);

  // Auto-save with debouncing - save workflow config whenever nodes/edges change
  React.useEffect(() => {
    if (!selectedWorkflow?.id) return;

    // IMPORTANT: Only auto-save if user has actually made changes
    // This prevents auto-save on initial load
    if (!userHasMadeChangesRef.current) {
      return;
    }

    // Debounce auto-save by 2 seconds
    const autoSaveTimeout = setTimeout(() => {
      // Transform frontend format to backend format before saving
      const transformedNodes = transformToBackendFormat(nodes);
      const flowConfig = { nodes: transformedNodes, edges };

      // Set flag to prevent the response from reloading nodes/edges
      isAutoSavingRef.current = true;

      // Save to backend via WorkflowStore
      updateWorkflowConfig(selectedWorkflow.id, flowConfig);

      // Reset the user changes flag after saving
      userHasMadeChangesRef.current = false;
    }, 2000); // 2 second debounce

    return () => clearTimeout(autoSaveTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, edges]); // Only depend on nodes/edges

  // Get default data for new nodes (BACKEND-ALIGNED)
  const getDefaultNodeData = (type) => {
    switch (type) {
      case 'eventWait':
        return {
          waitForEvent: 'appointment_completed',
          matchField: '',
          matchValue: '',
          timeoutDays: 30,
          onTimeout: 'fail',
          description: '',
        };

      case 'sendEmail':
        return {
          actionType: 'send_email',
          config: {
            subject: '',
            body: '',
            to: '{{customer.email}}'
          },
          label: 'Send Email',
          description: '',
        };

      case 'sendSMS':
        return {
          actionType: 'send_sms',
          config: {
            message: '',
            to: '{{customer.phone}}'
          },
          label: 'Send SMS',
          description: '',
        };

      case 'webhook':
        return {
          actionType: 'webhook',
          config: {
            url: '',
            method: 'POST',
            headers: {},
            payload: {}
          },
          label: 'Webhook',
          description: '',
        };

      case 'updateField':
        return {
          actionType: 'update_customer',
          config: {
            updates: {}
          },
          label: 'Update Field',
          description: '',
        };

      case 'wait':
        return {
          duration_value: 1,
          duration_unit: 'days',
          description: '',
        };

      case 'logicGate':
        return {
          gateType: 'AND',
          conditions: [],
          name: 'Logic Gate',
        };

      case 'entry':
        return {
          isEntry: true,
          triggerEvent: 'customer_created',
          conditions: [],
          label: 'Entry Point',
          description: '',
        };

      case 'exit':
        return {
          label: 'Complete',
          exitType: 'success',
          actions: [],
          description: '',
        };

      case 'httpRequest':
        return {
          label: 'HTTP Request',
          description: '',
          method: 'POST',
          url: '',
          headers: {},
          body: {},
          timeout: 30000,
          steps: [],
        };

      case 'webhookReceiver':
        return {
          label: 'Receive Callback',
          description: 'Wait for external callback',
          timeoutDays: 30,
          expectedFields: ['status', 'result_data'],
        };

      case 'scheduleTrigger':
        return {
          label: 'Schedule',
          schedule: {
            enabled: false,
            type: 'daily',
            time: '08:00',
            timezone: 'America/Chicago',
          },
        };

      default:
        return {};
    }
  };

  // Handle add node from toolbar
  const handleAddNode = useCallback((type) => {
    if (!reactFlowInstance) return;

    // VALIDATION: Prevent adding multiple entry nodes
    if (type === 'entry') {
      const existingEntryNode = nodes.find(node => node.type === 'entry');
      if (existingEntryNode) {
        setValidationError('Only one entry point allowed per workflow');
        // Clear error after 3 seconds
        setTimeout(() => setValidationError(null), 3000);
        return;
      }
    }

    // Calculate position (center of current viewport)
    const bounds = reactFlowWrapper.current.getBoundingClientRect();
    const position = reactFlowInstance.screenToFlowPosition({
      x: bounds.width / 2,
      y: bounds.height / 2,
    });

    // Create new node
    const newNode = {
      id: `${type}-${Date.now()}`,
      type,
      position,
      data: getDefaultNodeData(type),
    };

    setNodes((nds) => nds.concat(newNode));

    // Mark that user made changes
    if (hasLoadedInitialDataRef.current) {
      userHasMadeChangesRef.current = true;
    }
  }, [reactFlowInstance, nodes, setNodes]);


  return (
    <ReactFlowProvider>
      <div className="w-full h-full bg-slate-950 react-flow-wrapper" ref={reactFlowWrapper}>
      {/* Custom styles for React Flow attribution and handles */}
      <style>{`
        .react-flow-wrapper .react-flow__attribution {
          background: rgba(15, 23, 42, 0.9) !important;
          padding: 4px 8px !important;
          border-radius: 6px !important;
          border: 1px solid rgba(71, 85, 105, 0.5) !important;
          backdrop-filter: blur(8px) !important;
        }
        .react-flow-wrapper .react-flow__attribution a {
          color: #a78bfa !important;
          text-decoration: none !important;
          font-size: 10px !important;
        }
        .react-flow-wrapper .react-flow__attribution a:hover {
          color: #c4b5fd !important;
        }

        /* Connection state styling (from React Flow docs) */
        .react-flow__handle.connecting {
          background: #a78bfa;
        }

        .react-flow__handle.valid {
          background: #22c55e;
        }

        /* Connection line animation */
        @keyframes dash {
          to {
            stroke-dashoffset: -10;
          }
        }

        /* Selected edge styling - make it obvious when an edge is selected */
        .react-flow__edge.selected .react-flow__edge-path {
          stroke: #fbbf24 !important;
          stroke-width: 3 !important;
          filter: drop-shadow(0 0 8px rgba(251, 191, 36, 0.6));
        }

        .react-flow__edge.selected .react-flow__edge-path:hover {
          stroke: #fcd34d !important;
        }

        /* Make edge hover more obvious too */
        .react-flow__edge:hover .react-flow__edge-path {
          stroke: #c4b5fd !important;
          stroke-width: 3 !important;
        }
      `}</style>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        onInit={setReactFlowInstance}
        nodeTypes={nodeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        fitView
        attributionPosition="bottom-right"
        className="bg-slate-950"
        connectionLineStyle={{
          stroke: '#a78bfa',
          strokeWidth: 2,
        }}
        // Disable node/edge deletion for client_automation workflows
        deleteKeyCode={selectedWorkflow?.workflow_type === 'client_automation' ? null : 'Backspace'}
        nodesDeletable={selectedWorkflow?.workflow_type !== 'client_automation'}
        edgesDeletable={selectedWorkflow?.workflow_type !== 'client_automation'}
      >
        {/* Background Grid */}
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          color="#334155"
          className="bg-slate-950"
        />

        {/* Validation Error Alert (hidden for client_automation workflows) */}
        {validationError && selectedWorkflow?.workflow_type !== 'client_automation' && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="bg-red-500/90 backdrop-blur-sm border border-red-400 rounded-lg px-4 py-3 shadow-xl flex items-center gap-3 max-w-md">
              <AlertCircle className="w-5 h-5 text-white flex-shrink-0" />
              <div className="text-sm font-medium text-white">
                {validationError}
              </div>
            </div>
          </div>
        )}

        {/* Toolbar - Add Node Buttons */}
        {(
        <div className="absolute top-4 left-4 bg-slate-900/95 border border-slate-700 rounded-lg p-3 shadow-xl backdrop-blur-sm z-10">
          <div className="flex flex-col gap-2">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">
              Add Node
            </div>

            {/* Entry Node - Required */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleAddNode('entry')}
              className="justify-start border-green-500/30 text-green-300 hover:bg-green-500/10 relative"
            >
              <Plus className="w-3 h-3 mr-2" />
              Entry Point
              <span className="ml-auto text-[10px] text-green-400 font-semibold">(Required)</span>
            </Button>

            <div className="border-t border-slate-700 my-1" />
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Wait Nodes
            </div>

            <Button
              size="sm"
              variant="outline"
              onClick={() => handleAddNode('eventWait')}
              className="justify-start border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10"
            >
              <Plus className="w-3 h-3 mr-2" />
              Event Wait
            </Button>

            <div className="border-t border-slate-700 my-1" />
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Actions
            </div>

            <Button
              size="sm"
              variant="outline"
              onClick={() => handleAddNode('sendEmail')}
              className="justify-start border-blue-500/30 text-blue-300 hover:bg-blue-500/10"
            >
              <Plus className="w-3 h-3 mr-2" />
              Send Email
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={() => handleAddNode('sendSMS')}
              className="justify-start border-green-500/30 text-green-300 hover:bg-green-500/10"
            >
              <Plus className="w-3 h-3 mr-2" />
              Send SMS
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={() => handleAddNode('webhook')}
              className="justify-start border-purple-500/30 text-purple-300 hover:bg-purple-500/10"
            >
              <Plus className="w-3 h-3 mr-2" />
              Webhook
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={() => handleAddNode('updateField')}
              className="justify-start border-orange-500/30 text-orange-300 hover:bg-orange-500/10"
            >
              <Plus className="w-3 h-3 mr-2" />
              Update Field
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={() => handleAddNode('wait')}
              className="justify-start border-yellow-500/30 text-yellow-300 hover:bg-yellow-500/10"
            >
              <Plus className="w-3 h-3 mr-2" />
              Wait
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={() => handleAddNode('logicGate')}
              className="justify-start border-blue-500/30 text-blue-300 hover:bg-blue-500/10"
            >
              <Plus className="w-3 h-3 mr-2" />
              Logic Gate
            </Button>

            <div className="border-t border-slate-700 my-1" />
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Integrations
            </div>

            <Button
              size="sm"
              variant="outline"
              onClick={() => handleAddNode('httpRequest')}
              className="justify-start border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
            >
              <Plus className="w-3 h-3 mr-2" />
              HTTP Request
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={() => handleAddNode('webhookReceiver')}
              className="justify-start border-teal-500/30 text-teal-300 hover:bg-teal-500/10"
            >
              <Plus className="w-3 h-3 mr-2" />
              Webhook Receiver
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={() => handleAddNode('scheduleTrigger')}
              className="justify-start border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/10"
            >
              <Plus className="w-3 h-3 mr-2" />
              Schedule Trigger
            </Button>
          </div>
        </div>
        )}
      </ReactFlow>
    </div>
    </ReactFlowProvider>
  );
};

export default WorkflowEditor;
